"use client";

import { useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { clearTokens, getAccessToken, isAccessTokenExpiringSoon, refreshTokens } from "@/lib/auth";
import { useUIStore, type HandoffBundle } from "@/stores/ui-store";

const WS_URL = process.env.NEXT_PUBLIC_WS_URL || "http://localhost:8000";

export function useSocket() {
  const socketRef = useRef<Socket | null>(null);
  const qc = useQueryClient();
  const setHandoffBundle = useUIStore((s) => s.setHandoffBundle);

  useEffect(() => {
    let cancelled = false;
    let socket: Socket | null = null;
    let refreshingSocketAuth = false;

    const redirectToLogin = () => {
      clearTokens();
      window.location.href = "/login";
    };

    const registerSocketHandlers = (activeSocket: Socket) => {
      activeSocket.on("connect", () => {
        refreshingSocketAuth = false;
      });

      activeSocket.on("connect_error", async (error) => {
        if (cancelled || error.message !== "Invalid token" || refreshingSocketAuth) return;

        refreshingSocketAuth = true;
        activeSocket.disconnect();

        const refreshed = await refreshTokens();
        if (!refreshed) {
          if (!cancelled) redirectToLogin();
          return;
        }

        const token = getAccessToken();
        if (!token || cancelled) return;

        activeSocket.auth = { token };
        activeSocket.connect();
      });

      activeSocket.on("new_message", ({ conversation_id }: { conversation_id: string }) => {
        qc.invalidateQueries({ queryKey: ["conversations", conversation_id] });
        qc.invalidateQueries({ queryKey: ["conversations"] });
      });

      activeSocket.on(
        "message.status_updated",
        ({ conversation_id, delivery_status, error }: {
          conversation_id: string;
          message_id: string;
          delivery_status: string;
          error?: string;
        }) => {
          qc.invalidateQueries({ queryKey: ["conversations", conversation_id] });
          if (delivery_status === "failed") {
            toast.error(`Mensaje no entregado${error ? `: ${error}` : ""}`);
          }
        },
      );

      activeSocket.on("lead_updated", ({ lead_id }: { lead_id: string }) => {
        qc.invalidateQueries({ queryKey: ["leads", lead_id] });
        qc.invalidateQueries({ queryKey: ["leads"] });
        qc.invalidateQueries({ queryKey: ["conversations"] });
      });

      activeSocket.on("conversation_state_changed", ({ conversation_id }: { conversation_id: string }) => {
        qc.invalidateQueries({ queryKey: ["conversations", conversation_id] });
        qc.invalidateQueries({ queryKey: ["conversations"] });
        qc.invalidateQueries({ queryKey: ["alerts"] });
        qc.invalidateQueries({ queryKey: ["alerts-count"] });
      });

      activeSocket.on(
        "lead.pipeline_updated",
        ({ lead_id, stage }: { lead_id: string; stage: string }) => {
          qc.invalidateQueries({ queryKey: ["leads", lead_id] });
          qc.invalidateQueries({ queryKey: ["leads"] });
          toast.info(`Etapa CRM actualizada: ${stage}`);
        },
      );

      activeSocket.on("new_lead", () => {
        qc.invalidateQueries({ queryKey: ["leads"] });
        qc.invalidateQueries({ queryKey: ["dashboard"] });
      });

      activeSocket.on("handoff_bundle", (bundle: HandoffBundle) => {
        setHandoffBundle(bundle);
        qc.invalidateQueries({ queryKey: ["conversations", bundle.conversation_id] });
        qc.invalidateQueries({ queryKey: ["conversations"] });
      });

      activeSocket.on(
        "sentiment_alert",
        ({
          conversation_id,
          lead_id,
          lead_name,
          lead_phone,
          alert_type,
          alert_label,
        }: {
          conversation_id: string;
          lead_id: string;
          lead_name: string | null;
          lead_phone: string | null;
          alert_type: string;
          alert_label: string;
        }) => {
          const name = lead_name ?? lead_phone ?? "Lead desconocido";
          const isUrgent = ["unsubscribe", "unsubscribe_human", "urgent"].includes(alert_type);
          const toastFn = isUrgent ? toast.error : toast.warning;
          toastFn(`${alert_label} — ${name}`, {
            description: alert_type === "unsubscribe" || alert_type === "unsubscribe_human"
              ? "El lead ha solicitado darse de baja."
              : "Requiere atención del equipo humano.",
            action: {
              label: "Ver",
              onClick: () => { window.location.href = `/conversations?lead_id=${lead_id}`; },
            },
            duration: 10000,
          });
          qc.invalidateQueries({ queryKey: ["conversations", conversation_id] });
          qc.invalidateQueries({ queryKey: ["conversations"] });
          qc.invalidateQueries({ queryKey: ["alerts"] });
          qc.invalidateQueries({ queryKey: ["alerts-count"] });
        },
      );
    };

    const connectSocket = async () => {
      let token = getAccessToken();
      if (!token) return;

      if (isAccessTokenExpiringSoon()) {
        const refreshed = await refreshTokens();
        if (!refreshed) {
          if (!cancelled) redirectToLogin();
          return;
        }
        token = getAccessToken();
      }

      if (!token || cancelled) return;

      socket = io(WS_URL, {
        path: "/ws/socket.io",
        auth: { token },
        transports: ["websocket", "polling"],
      });

      socketRef.current = socket;
      registerSocketHandlers(socket);
    };

    void connectSocket();

    return () => {
      cancelled = true;
      socket?.disconnect();
      socketRef.current = null;
    };
  }, [qc, setHandoffBundle]);

  return socketRef.current;
}
